import { useEffect, useMemo, useState } from 'react';
import {
  useGetApiV1MissionsMissionIdCoverage,
  useGetApiV1MissionsMissionIdPositions,
} from '@/shared/api/generated/missions/missions';
import { LiveEvery, liveEvery } from '@/shared/lib/live';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { MissionMap } from '@/features/missions/ui/MissionMap';
import type { MissionSectorDto } from '@/shared/api/generated/model';
import {
  mergeTracks,
  useMissionTracks,
  type LiveTrack,
  type WatchedMember,
} from '@/features/missions/model/use-mission-tracks';

/**
 * The coverage picture for one mission (§9): the map, and the figures under it.
 *
 * ⚠️ **There is still no basemap, and the map is useful anyway.** §10 serves tiles from region
 * `.pmtiles` packs that do not exist yet, and §12.12 forbids reaching for somebody else's. What an
 * operator needs at three in the morning is the geometry — which ground has been walked and which
 * has not — and that renders on a blank ground exactly as well as on a satellite one. The app
 * reached the same conclusion first.
 *
 * ⚠️ Both overlays are computed **server-side by PostGIS**, never in the browser: the operator and
 * the rescuer must be looking at the same ground.
 */
export function MissionCoverage({
  missionId,
  area,
  sectors,
  watched = [],
}: {
  missionId: string;

  /**
   * The search area's outline, when the caller has one.
   *
   * ⚠️ Optional because the board's row genuinely does not have it: `MissionListItemDto` carries a
   * centre and a size, not a boundary — a page of twenty outlines is a payload nobody scrolling a
   * list asked for. The row shows the figures; the mission's own screen shows the map.
   */
  area?: number[][];

  /** The sectors still held, drawn as labelled outlines (§5.9). */
  sectors?: MissionSectorDto[];

  /**
   * The members whose live positions belong on this map.
   *
   * ⚠️ The ones still ON the mission, not everyone who ever was. §8.3 refuses a subscription to a
   * removed member's track topic, so passing them would be one denied subscription per removal —
   * and with `deny_action = disconnect` that costs the whole connection, not just the request.
   */
  watched?: WatchedMember[];
}) {
  const { t } = useLocale();
  const coverage = useGetApiV1MissionsMissionIdCoverage(
    missionId,
    undefined,
    liveEvery(LiveEvery.coverage),
  );

  // ⚠️ The record behind the feed — see `mergeTracks` for why the feed alone drew an empty map.
  const positions = useGetApiV1MissionsMissionIdPositions(missionId, liveEvery(LiveEvery.positions));

  // ⚠️ Checked rather than trusted. This read is the newest thing on the page, and a server one
  // release behind answers it with a problem document — which must cost the starting picture, not
  // the whole mission screen.
  const last = Array.isArray(positions.data) ? positions.data : undefined;

  // ⚠️ The caller's list when it has one, the record's otherwise. Both name only people still ON
  // the search, which is what §8.3 will let this panel subscribe to.
  const following = useMemo<WatchedMember[]>(
    () =>
      watched.length > 0
        ? watched
        : (last ?? []).map((member) => ({
            userId: member.userId,
            fullName: member.fullName,
          })),
    [watched, last],
  );

  const live = useMissionTracks(missionId, following);
  const now = useNow();

  const tracks = useMemo(
    () => aged(mergeTracks(last, live), now, t.missions),
    [last, live, now, t.missions],
  );

  if (coverage.isPending) {
    return <p className="text-sm text-muted-foreground">{t.app.loading}</p>;
  }

  if (coverage.isError) {
    return <p className="text-sm text-muted-foreground">{t.missions.coverageUnavailable}</p>;
  }

  const { coveredAreaKm2, gapAreaKm2, missionAreaKm2 } = coverage.data;

  return (
    <div className="flex flex-col gap-3">
      {area && (
        <MissionMap
          area={area}
          coverageGeoJson={coverage.data.coverageGeoJson}
          gapGeoJson={coverage.data.gapGeoJson}
          sectors={sectors}
          tracks={tracks}
        />
      )}

      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <Figure
          label={t.missions.coverageCovered}
          value={`${coveredAreaKm2.toFixed(2)} ${t.missions.areaUnit}`}
        />
        <Figure
          label={t.missions.coverageGap}
          value={`${gapAreaKm2.toFixed(2)} ${t.missions.areaUnit}`}
        />
        <Figure
          label={t.missions.coverageArea}
          value={`${missionAreaKm2.toFixed(2)} ${t.missions.areaUnit}`}
        />
        <Figure label={t.missions.coverageMembers} value={String(coverage.data.memberCount)} />
        <Figure label={t.missions.coveragePoints} value={String(coverage.data.trackPointCount)} />
        <Figure
          label={t.missions.coverageRadius}
          value={`${coverage.data.visibilityRadiusM} ${t.missions.metres}`}
        />
      </div>

      {/* ⚠️ **No percentage and no proportion bar, and this is a rule rather than a preference.**

          PROJECT.md §9 carries the heading "Never display a coverage percentage" and gives the
          reason: the swept corridor is a direct multiple of an assumed visibility radius that is
          honest to within a factor of eight, so a rescuer who reads a confident number leaves having
          swept a fifth of the ground. Both were here — the covered figure carried a `%` note and a
          meter drew the same share — printed directly above the sentence that says §9 refuses it.

          What replaces it is already on this screen: the corridor drawn on the map at the width the
          current assumption implies, the two areas as areas, and the assumption stated below. */}

      {/* ⚠️ The assumption, stated. §9 refuses to give this a percentage because the swept corridor
          is a direct multiple of a radius that is honest to within a factor of eight between midday
          on gravel and dusk among dunes — and an operator who reads a confident number stops
          searching having covered a fifth of the ground. */}
      <p className="text-xs text-muted-foreground">
        {t.missions.coverageAssumption}
      </p>
    </div>
  );
}

/**
 * The clock every marker's age is read against, moved by a timer rather than by the data.
 *
 * ⚠️ A marker has to start saying «قبل ٥ د» precisely when NOTHING arrives — the one moment no new
 * data would ever redraw it.
 */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  return now;
}

/**
 * Each marker's name, with how long ago it was true once that is long enough to matter.
 *
 * ⚠️ **A position with no age on it is read as where the person IS.** Under two minutes that is
 * right, and saying so on every marker would bury the ones that are not. Past it the label says
 * how old — because a marker that has not moved for twenty minutes is either a volunteer sitting
 * with a casualty or a phone that died, and both are somewhere to send help.
 */
function aged(
  tracks: Record<string, LiveTrack>,
  now: number,
  words: { positionMinutesAgo: string; positionHoursAgo: string },
): Record<string, LiveTrack> {
  const labelled: Record<string, LiveTrack> = {};

  for (const track of Object.values(tracks)) {
    const minutes = Math.floor((now - Date.parse(track.recordedAt)) / 60_000);

    const age =
      !Number.isFinite(minutes) || minutes < 2
        ? null
        : minutes < 60
          ? words.positionMinutesAgo.replace('{n}', String(minutes))
          : words.positionHoursAgo.replace('{n}', String(Math.floor(minutes / 60)));

    labelled[track.userId] = age ? { ...track, fullName: `${track.fullName} · ${age}` } : track;
  }

  return labelled;
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="tabular font-semibold">
        {value}
        {note ? <span className="ps-2 text-xs font-normal text-muted-foreground">{note}</span> : null}
      </span>
    </div>
  );
}
